import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="sidenav"
//
// Three behaviors share this controller:
//
// LG+ expanded (>= 1024px, default): Vertical column with full section
//   labels. Sections are collapsible; collapsed state persists in a
//   cookie. Toggles `lg:hidden` on each link and `lg:-rotate-90` on the
//   chevron.
//
// LG+ rail (>= 1024px, sidenav-rail class on aside): Narrow icon-only
//   rail. Clicking a section icon opens a fixed-positioned flyout panel
//   to the right of the icon with that section's links. Rail state
//   persists in a cookie.
//
// Narrow (< 1024px): Horizontal scrolling header. Each section's
//   children live in a fixed-positioned dropdown anchored under its
//   button. Only one panel can be open at a time.
//
// Flyout mode (rail OR narrow): clicking outside or following a link
// closes any open panel. JS sets position:fixed from the button rect
// so the panel escapes the aside's overflow-x-auto clipping.
const COOKIE_NAME = "admin_sidenav_collapsed"
const RAIL_COOKIE_NAME = "admin_sidenav_rail"
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365 // 1 year
const LG_BREAKPOINT = 1024
const PANEL_GAP = 0

export default class extends Controller {
  static targets = ["link", "sectionHeader", "sectionItem", "chevron", "sectionPanel"]

  connect() {
    this.handleClickOutside = (event) => {
      if (!this.isFlyoutMode()) return
      if (this.element.contains(event.target)) return
      this.closeAllPanels()
    }
    document.addEventListener("click", this.handleClickOutside)
  }

  disconnect() {
    document.removeEventListener("click", this.handleClickOutside)
  }

  isRail() {
    // Rail class persists in a cookie across viewports, but the rail
    // styling/behavior only applies at lg+. On narrow we always want
    // the narrow flyout branch (below button) regardless of cookie.
    return window.innerWidth >= LG_BREAKPOINT && this.element.classList.contains("sidenav-rail")
  }

  isFlyoutMode() {
    return window.innerWidth < LG_BREAKPOINT || this.isRail()
  }

  toggleClass(link) {
    this.linkTargets.forEach((el) => {
      el.classList.add("inactive-sidenav")
      el.classList.remove("active-sidenav")
    })

    link.classList.remove("inactive-sidenav")
    link.classList.add("active-sidenav")
  }

  linkClick(event) {
    const link = event.currentTarget
    this.toggleClass(link)
    if (this.isFlyoutMode()) {
      this.closeAllPanels()
    }
  }

  toggleSection(event) {
    const button = event.currentTarget
    const key = button.dataset.sectionKey
    if (!key) return

    if (this.isFlyoutMode()) {
      const targetPanel = this.sectionPanelTargets.find((p) => p.dataset.sectionKey === key)
      if (!targetPanel) return
      const wasOpen = !targetPanel.classList.contains("hidden")
      this.closeAllPanels()
      if (!wasOpen) {
        this.openPanel(targetPanel, button)
        const chevron = this.chevronTargets.find((c) => c.dataset.sectionKey === key)
        if (chevron) chevron.classList.add("max-lg:rotate-180")
      }
      return
    }

    const collapsed = this.collapsedSet()
    if (collapsed.has(key)) {
      collapsed.delete(key)
    } else {
      collapsed.add(key)
    }
    this.writeCollapsed(collapsed)
    this.applySectionState(key, collapsed.has(key))
  }

  openPanel(panel, button) {
    const rect = button.getBoundingClientRect()
    panel.style.position = "fixed"
    if (this.isRail()) {
      panel.style.top = `${rect.top}px`
      panel.style.left = `${rect.right + PANEL_GAP}px`
    } else {
      panel.style.top = `${rect.bottom + PANEL_GAP}px`
      panel.style.left = `${rect.left}px`
    }
    panel.classList.remove("hidden")
  }

  closeAllPanels() {
    this.sectionPanelTargets.forEach((p) => {
      p.classList.add("hidden")
      p.style.position = ""
      p.style.top = ""
      p.style.left = ""
    })
    this.chevronTargets.forEach((c) => c.classList.remove("max-lg:rotate-180"))
  }

  toggleRail() {
    const railed = this.element.classList.toggle("sidenav-rail")
    this.element.classList.toggle("lg:w-16", railed)
    this.element.classList.toggle("lg:w-64", !railed)
    this.sectionPanelTargets.forEach((p) => p.classList.toggle("lg:contents", !railed))
    this.closeAllPanels()
    this.writeRailCookie(railed)
  }

  writeRailCookie(railed) {
    if (railed) {
      document.cookie = `${RAIL_COOKIE_NAME}=1; path=/; max-age=${COOKIE_MAX_AGE}; samesite=lax`
    } else {
      document.cookie = `${RAIL_COOKIE_NAME}=; path=/; max-age=0; samesite=lax`
    }
  }

  applySectionState(key, isCollapsed) {
    this.sectionItemTargets
      .filter((el) => el.dataset.sidenavSection === key)
      .forEach((el) => el.classList.toggle("lg:hidden", isCollapsed))

    this.chevronTargets
      .filter((el) => el.dataset.sectionKey === key)
      .forEach((el) => el.classList.toggle("lg:-rotate-90", isCollapsed))
  }

  collapsedSet() {
    const raw = this.readCookie(COOKIE_NAME)
    try {
      return new Set(raw ? JSON.parse(decodeURIComponent(raw)) : [])
    } catch (e) {
      return new Set()
    }
  }

  writeCollapsed(set) {
    const value = encodeURIComponent(JSON.stringify([...set]))
    document.cookie = `${COOKIE_NAME}=${value}; path=/; max-age=${COOKIE_MAX_AGE}; samesite=lax`
  }

  readCookie(name) {
    const prefix = `${name}=`
    const cookie = document.cookie.split("; ").find((c) => c.startsWith(prefix))
    return cookie ? cookie.slice(prefix.length) : null
  }
}
